import { Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import Button from "../../components/GeneralComponents/Button";
import DomeCareTitle from "../../components/GeneralComponents/DomeCareTitle";
import ErrorsWrapper from "../../components/GeneralComponents/ErrorsWrapper";
import FormObserver from "../../components/GeneralComponents/FormObserver";
import GeneralInput from "../../components/GeneralComponents/GeneralInput";
import ModalPhoneNumberInput from "../../components/GeneralComponents/ModalPhoneNumberInput";
import { useAuthContext } from "../../context/AuthContext";
import useToast from "../../hooks/useToast";
import {
  getRegisterUserOTPInfo,
  login,
  setIsAuthenticated,
  setIsCompleteRegister,
  setIsUserForgettingPassword,
  setIsUserRegistering,
  setRegistrationStep,
  setRole,
  updateOTPDuration,
} from "../../redux/slices/authSlice";
import { RootState } from "../../redux/store";
import { LoginFormValues } from "../../types/api";
import { useUserLogin } from "../../utils/api/AuthAPIs/useUserLogin";
import { useCountriesQuery } from "../../utils/api/General/useCountries";
import {
  ArabicCode,
  DefaultNavigateDelay,
  DefaultResponseCodeForContinueRegistration,
  DefaultResponseCodeForContinueStaffRegisteration,
  GlobalToasterDuration,
  ToasterErrorImageUrl,
} from "../../utils/constants/CommonConstants";
import useLoginSchema from "../../utils/validations/auth/loginSchema";
import { getTimezoneOffset } from "../../utils/Functions";

const Login: React.FC = () => {
  const { t, i18n } = useTranslation("translation");
  const { showToast } = useToast();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loginSchema = useLoginSchema();
  //?====================Redux States==========================
  const { countryId } = useSelector((state: RootState) => state.auth);
  //?=======================States==========================
  const { changeShowLeaveAuthRoutePopupAuthState } = useAuthContext();
  const [initialValues, setInitialValues] = useState<LoginFormValues>({
    phoneNumber: "",
    password: "",
  });
  const [isRegexError, setIsRegexError] = useState<boolean>(false);
  //?================= API call for login======================
  const {
    error: UserLoginError,
    status: UserLoginStatus,
    mutate: UserLoginMutate,
  } = useUserLogin();
  //?=================Disable Navigate Forward in the browser======================
  useEffect(() => {
    dispatch(setRegistrationStep("register"));
    dispatch(updateOTPDuration(0));
    window.history.replaceState(null, "", "/");
    const handlePopState = (event: PopStateEvent) => {
      window.history.pushState(null, "", window.location.href);
    };
    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [dispatch]);

  useEffect(() => {
    changeShowLeaveAuthRoutePopupAuthState(false);
    localStorage.removeItem("otpDuration");
    dispatch(setIsUserRegistering(false));
    dispatch(setIsUserForgettingPassword(false));
    dispatch(
      setIsCompleteRegister({
        isCompleteRegister: false,
      }),
    );
  }, []);
  //?===================== Handle form submission====================
  const handleSubmit = () => {
    if (!isRegexError) {
      UserLoginMutate(
        {
          Identification: initialValues.phoneNumber,
          Password: initialValues.password,
          CountryId: countryId,
          Language: i18n.language.slice(0, 2),
          TimeZone: getTimezoneOffset(),
        },
        {
          onSuccess: (response) => {
            if (response.Status) {
              dispatch(
                setIsAuthenticated({
                  isAuthenticated: true,
                }),
              );
              const userData = response.Data;
              if (!userData) {
                console.error("User data is null or undefined");
                return;
              }

              const loginPayload = {
                id: userData.Id,
                phoneNumber: userData.PhoneNumber,
                fullName: userData.FullName,
                token: userData.Token,
                isVerified: userData.IsVerified,
                currentFacility: userData.CurrentFacility || {},
              };

              const FacilityType = userData.CurrentFacility?.FacilityType?.Code;
              dispatch(login(loginPayload));
              dispatch(setRole(FacilityType));

              if (
                response.Code === DefaultResponseCodeForContinueRegistration
              ) {
                dispatch(
                  setIsCompleteRegister({
                    isCompleteRegister: true,
                    registrationStep: "accountType",
                  }),
                );
                window.setTimeout(() => {
                  navigate("/auth/facility-type", { replace: true });
                }, DefaultNavigateDelay);
                return;
              }

              if (
                response.Code ===
                DefaultResponseCodeForContinueStaffRegisteration
              ) {
                dispatch(setIsUserRegistering(true));
                dispatch(
                  getRegisterUserOTPInfo({
                    userId: response.Data.Id,
                    otpDuration: response.Data.Duration,
                    otpType: "VerifyStaffOTP",
                  }),
                );
                navigate("/auth/otp", { replace: true });
                return;
              }

              if (FacilityType === "warehouse" || FacilityType === "pharmacy") {
                navigate("/", { replace: true });
              }
            }
          },
          onError: (response) => {
            console.error(
              "ERROR",
              response?.message || "Unknown error occurred",
            );
            showToast(
              t("login_error"),
              t("failed_to_login"),
              "error",
              GlobalToasterDuration,
              "bottom-right",
              ToasterErrorImageUrl,
            );
          },
        },
      );
    }
  };

  //?======================== Fetch countries list============================
  const { data: CountriesData } = useCountriesQuery();

  return (
    <article
      dir={i18n.language === ArabicCode ? "rtl" : "ltr"}
      className="z-20 flex w-full flex-col gap-6 px-8 py-10 xl:w-[75%]"
    >
      <div className="flex flex-col gap-6">
        {/* <DomeCareTitle /> */}
        <p className="py-8 text-center text-2xl font-bold text-headingTextColor">
          {t("login_to_domecare")}
        </p>
      </div>
      <Formik
        initialValues={initialValues}
        validationSchema={loginSchema}
        onSubmit={handleSubmit}
      >
        {({ values, handleSubmit }) => (
          <Form
            className="flex flex-col gap-5"
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                handleSubmit();
              }
            }}
          >
            <div className="flex flex-col gap-5">
              <ModalPhoneNumberInput
                name="phoneNumber"
                placeholder={t("phone_number")}
                countriesList={CountriesData?.Data}
                autoFocus={true}
                required={true}
                getRegexError={(isRegexError) => {
                  setIsRegexError(isRegexError);
                }}
              />
            </div>
            <div className="flex flex-col gap-4">
              <GeneralInput
                name="password"
                type="password"
                placeholder={t("password")}
                required
                resetOnchangeLanguage={true}
              />
              <div className="flex w-full items-center justify-end">
                <Link
                  to={"/auth/forget-password"}
                  onClick={() => {
                    dispatch(setIsUserForgettingPassword(true));
                  }}
                  className="text-sm font-normal text-headingTextColor underline"
                >
                  {t("forget_password")}
                </Link>
              </div>
            </div>

            <Button
              title={UserLoginStatus === "pending" ? t("loading") : t("login")}
              type="submit"
              variant="primary"
              extraClassName="shadow-cta"
              disabled={UserLoginStatus === "pending"}
            />
            <FormObserver setInitialValues={setInitialValues} values={values} />
          </Form>
        )}
      </Formik>
      <div className="flex items-center justify-center gap-2 px-8 py-3">
        <p className="text-base font-normal text-headingTextColor">
          {t("dont_have_account")}
        </p>
        <Link
          to={"/auth/register"}
          className="flex h-12 items-center py-1 text-base font-bold text-secondaryMain underline"
        >
          {t("create_new_account")}
        </Link>
      </div>
      {/* //!==========================Errors============================== */}
      {UserLoginError && <ErrorsWrapper message={UserLoginError?.message} />}
    </article>
  );
};

export default Login;
